import { Textarea, Tooltip } from '@repo/go-ui';
import type { GridRenderEditCellParams, GridValidRowModel } from '@repo/go-table';
import { useCallback, useEffect, useLayoutEffect, useState, type ComponentProps } from 'react';

type TextareaOnChange = NonNullable<ComponentProps<typeof Textarea>['onChange']>;
export function TableTextareaCell(
  props: GridRenderEditCellParams<GridValidRowModel, string> & { onChange?: (value: string) => void },
) {
  const { id, field, value, colDef, hasFocus, api, onChange } = props;
  const [valueState, setValueState] = useState(value);
  const [inputRef, setInputRef] = useState<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    if (hasFocus && inputRef) {
      inputRef.focus();
    }
  }, [hasFocus, inputRef]);

  useEffect(() => {
    if (inputRef) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && event.shiftKey) {
          event.stopPropagation();
        }
      };
      inputRef.addEventListener('keydown', handleKeyDown);
      return () => {
        inputRef.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [inputRef]);

  const handleChange = useCallback<TextareaOnChange>(
    (value, event) => {
      setValueState(value);
      onChange?.(value);
      api?.setEditCellValue({ id, field, value, debounceMs: 200 }, event);
    },
    [field, id, api, onChange],
  );

  return (
    <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
      <Tooltip
        open
        placement="bottom-start"
        title={
          <Textarea
            value={valueState}
            onChange={handleChange}
            ref={(ref) => setInputRef(ref)}
            style={{ width: '100%', maxWidth: '100%' }}
          />
        }
        style={{ padding: 0, border: 'none', width: colDef.computedWidth - 2 }}
        withArrow={false}
      >
        <div
          style={{
            height: 0,
            width: colDef.computedWidth,
            display: 'block',
            position: 'absolute',
            top: 0,
          }}
        />
      </Tooltip>
    </div>
  );
}
